
import "./Navigation.css"

import React, {Context, createContext, useContext, useEffect, useState} from 'react';
import { animated } from 'react-spring';
import { useDrag } from '@use-gesture/react';

import './Navigation.css'
import {NavigationFooterProps} from "./NavigationFooter";
import {SetRootBackButtonAction} from "../Elements/BackButtonProvider";

export let SetRootNavigationSelected: Context<(index: number) => void>;
export let SetNavigationDragEnabled: Context<(enabled: boolean) => void>;

export type NavigationProps = {
    screens: JSX.Element[],
    navigation: (navProps: NavigationFooterProps) => JSX.Element
    orientation: "up" | "down",
    useGestures: boolean,
    isMain: boolean,
    navigationStackHeight: string
}

function NavigationInner(props : NavigationProps) {
    const [selected, setSelected] = useState(0);
    const [dragStride, setDragStride] = useState(0);
    const [isDragEnabled, setIsDragEnabled] = useState(props.useGestures)

    useEffect(() => {
        setIsDragEnabled(props.useGestures);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const SLIDE_BEGIN_STRIDE = 15;
    const SLIDE_SWITCH_STRIDE = 50;

    const setSelectedWrapped = (index : number) => {
        setSelected(index);
    }

    if (props.isMain) {
        SetRootNavigationSelected = createContext((index: number) => setSelectedWrapped(index));
    }

    if (props.useGestures) {
        SetNavigationDragEnabled = createContext(v => {
            setIsDragEnabled(v);
        });
    }

    const setRootBackButtonAction = useContext(SetRootBackButtonAction)

    // Indication of root navigation, TODO: make it more meaningful
    useEffect(() => {
        if (props.useGestures) {
            const entry = selected !== 0 ? {
                fn: () => {setSelectedWrapped(0)},
                id: "go-to-main"
            } : null
            setRootBackButtonAction(entry)
        }
    }, [props.useGestures, selected, setRootBackButtonAction]); // eslint-disable-line


    // It returns stuff to bind, not void
    // noinspection JSVoidFunctionReturnValueUsed
    const bind = useDrag(({ down, movement: [mx], cancel }) => {
        if (!isDragEnabled) {
            cancel();
            return;
        }
        if (Math.abs(mx) < SLIDE_BEGIN_STRIDE) return;

        if (mx > 0 && selected <= 0) {
            setDragStride(Math.pow(mx, 0.7));
            if (!down) setDragStride(0);
            return;
        }

        if (mx < 0 && selected >= props.screens.length - 1) {
            setDragStride(-Math.pow(Math.abs(mx), 0.7));
            if (!down) setDragStride(0);
            return;
        }

        setDragStride(mx - (Math.sign(mx) * SLIDE_BEGIN_STRIDE))

        if (!down) {
            if (mx > SLIDE_SWITCH_STRIDE) setSelectedWrapped(selected - 1);
            if (mx < -SLIDE_SWITCH_STRIDE) setSelectedWrapped(selected + 1);
            setDragStride(0);
        }
    }, {axis: 'x'});

    const strideStyle = (relative : number) => {
        return {
            left: `calc(${relative - selected} * 100% + ${dragStride}px)`,
            transition: Math.abs(dragStride) < SLIDE_BEGIN_STRIDE ? "0.2s" : "0s"
        }
    }

    const navProps: NavigationFooterProps = {
        selected: selected,
        setSelected: setSelectedWrapped
    }
    const mainContent = <animated.div {...bind()} style={{height: "100%"}}>
        <div className="navigation-stack" style={{height: props.navigationStackHeight}}>
            {props.screens.map((screen: JSX.Element, i) =>
                <React.Fragment key={i}>
                    <div className="navigation-screen" style={{height: props.navigationStackHeight, ...strideStyle(i)}}>
                        {screen}
                    </div>
                </React.Fragment>
            )}
        </div>
    </animated.div>

    if (props.orientation === "up") {
        return (<>
            {props.navigation(navProps)}
            {mainContent}
        </>);
    }

    return (<>
        {mainContent}
        {props.navigation(navProps)}
    </>);
}

export const Navigation = React.memo(NavigationInner, (a1, a2) => true);